import React from 'react';
import { DollarSignIcon, UsersIcon, TrendingUpIcon } from 'lucide-react';
interface InputParams {
  revenueTarget: number;
  segmentMix: {
    enterprise: number;
    midMarket: number;
    smb: number;
  };
  averageDealSize: {
    enterprise: number;
    midMarket: number;
    smb: number;
  };
  conversionRates: {
    sql_to_opportunity: number;
    opportunity_to_close: number;
  };
}
interface InputParametersPanelProps {
  params: InputParams;
  onChange: (params: InputParams) => void;
}
export function InputParametersPanel({
  params,
  onChange
}: InputParametersPanelProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };
  return <div className="p-6 space-y-6" data-id="element-1411">
      <div data-id="element-1412">
        <h2 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-1413">
          Input Parameters
        </h2>
        <p className="text-sm text-gray-600" data-id="element-1414">
          Configure your GTM planning assumptions
        </p>
      </div>
      {/* Revenue Target */}
      <div className="space-y-3" data-id="element-1415">
        <div className="flex items-center gap-2" data-id="element-1416">
          <DollarSignIcon className="w-5 h-5 text-red-600" data-id="element-1417" />
          <label className="text-sm font-semibold text-gray-900" data-id="element-1418">
            Annual Revenue Target
          </label>
        </div>
        <input type="number" value={params.revenueTarget} onChange={e => onChange({
        ...params,
        revenueTarget: Number(e.target.value)
      })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-lg font-semibold" data-id="element-1419" />
        <p className="text-sm text-gray-600" data-id="element-1420">
          Target: {formatCurrency(params.revenueTarget)}
        </p>
      </div>
      {/* Segment Mix */}
      <div className="space-y-3" data-id="element-1421">
        <div className="flex items-center gap-2" data-id="element-1422">
          <UsersIcon className="w-5 h-5 text-red-600" data-id="element-1423" />
          <label className="text-sm font-semibold text-gray-900" data-id="element-1424">
            Segment Mix (%)
          </label>
        </div>
        <div className="space-y-3" data-id="element-1425">
          <div data-id="element-1426">
            <div className="flex items-center justify-between mb-2" data-id="element-1427">
              <span className="text-sm text-gray-700" data-id="element-1428">Enterprise</span>
              <span className="text-sm font-semibold text-gray-900" data-id="element-1429">
                {params.segmentMix.enterprise}%
              </span>
            </div>
            <input type="range" min="0" max="100" value={params.segmentMix.enterprise} onChange={e => onChange({
            ...params,
            segmentMix: {
              ...params.segmentMix,
              enterprise: Number(e.target.value)
            }
          })} className="w-full" data-id="element-1430" />
          </div>
          <div data-id="element-1431">
            <div className="flex items-center justify-between mb-2" data-id="element-1432">
              <span className="text-sm text-gray-700" data-id="element-1433">Mid-Market</span>
              <span className="text-sm font-semibold text-gray-900" data-id="element-1434">
                {params.segmentMix.midMarket}%
              </span>
            </div>
            <input type="range" min="0" max="100" value={params.segmentMix.midMarket} onChange={e => onChange({
            ...params,
            segmentMix: {
              ...params.segmentMix,
              midMarket: Number(e.target.value)
            }
          })} className="w-full" data-id="element-1435" />
          </div>
          <div data-id="element-1436">
            <div className="flex items-center justify-between mb-2" data-id="element-1437">
              <span className="text-sm text-gray-700" data-id="element-1438">SMB</span>
              <span className="text-sm font-semibold text-gray-900" data-id="element-1439">
                {params.segmentMix.smb}%
              </span>
            </div>
            <input type="range" min="0" max="100" value={params.segmentMix.smb} onChange={e => onChange({
            ...params,
            segmentMix: {
              ...params.segmentMix,
              smb: Number(e.target.value)
            }
          })} className="w-full" data-id="element-1440" />
          </div>
        </div>
      </div>
      {/* Average Deal Size */}
      <div className="space-y-3" data-id="element-1441">
        <label className="text-sm font-semibold text-gray-900" data-id="element-1442">
          Average Deal Size
        </label>
        <div className="space-y-3" data-id="element-1443">
          <div data-id="element-1444">
            <label className="block text-xs text-gray-600 mb-1" data-id="element-1445">
              Enterprise
            </label>
            <input type="number" value={params.averageDealSize.enterprise} onChange={e => onChange({
            ...params,
            averageDealSize: {
              ...params.averageDealSize,
              enterprise: Number(e.target.value)
            }
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-1446" />
          </div>
          <div data-id="element-1447">
            <label className="block text-xs text-gray-600 mb-1" data-id="element-1448">
              Mid-Market
            </label>
            <input type="number" value={params.averageDealSize.midMarket} onChange={e => onChange({
            ...params,
            averageDealSize: {
              ...params.averageDealSize,
              midMarket: Number(e.target.value)
            }
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-1449" />
          </div>
          <div data-id="element-1450">
            <label className="block text-xs text-gray-600 mb-1" data-id="element-1451">SMB</label>
            <input type="number" value={params.averageDealSize.smb} onChange={e => onChange({
            ...params,
            averageDealSize: {
              ...params.averageDealSize,
              smb: Number(e.target.value)
            }
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-1452" />
          </div>
        </div>
      </div>
      {/* Conversion Rates */}
      <div className="space-y-3" data-id="element-1453">
        <div className="flex items-center gap-2" data-id="element-1454">
          <TrendingUpIcon className="w-5 h-5 text-red-600" data-id="element-1455" />
          <label className="text-sm font-semibold text-gray-900" data-id="element-1456">
            Conversion Assumptions
          </label>
        </div>
        <div className="space-y-3" data-id="element-1457">
          <div data-id="element-1458">
            <div className="flex items-center justify-between mb-2" data-id="element-1459">
              <span className="text-sm text-gray-700" data-id="element-1460">SQL → Opportunity</span>
              <span className="text-sm font-semibold text-gray-900" data-id="element-1461">
                {params.conversionRates.sql_to_opportunity}%
              </span>
            </div>
            <input type="range" min="0" max="100" value={params.conversionRates.sql_to_opportunity} onChange={e => onChange({
            ...params,
            conversionRates: {
              ...params.conversionRates,
              sql_to_opportunity: Number(e.target.value)
            }
          })} className="w-full" data-id="element-1462" />
          </div>
          <div data-id="element-1463">
            <div className="flex items-center justify-between mb-2" data-id="element-1464">
              <span className="text-sm text-gray-700" data-id="element-1465">Opportunity → Close</span>
              <span className="text-sm font-semibold text-gray-900" data-id="element-1466">
                {params.conversionRates.opportunity_to_close}%
              </span>
            </div>
            <input type="range" min="0" max="100" value={params.conversionRates.opportunity_to_close} onChange={e => onChange({
            ...params,
            conversionRates: {
              ...params.conversionRates,
              opportunity_to_close: Number(e.target.value)
            }
          })} className="w-full" data-id="element-1467" />
          </div>
        </div>
      </div>
    </div>;
}